import {FocusMonitor} from '@angular/cdk/a11y';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  Optional,
  Self,
} from '@angular/core';
import {
  ControlValueAccessor,
  NgControl,
} from '@angular/forms';
import {MAT_FORM_FIELD, MatFormField, MatFormFieldControl, MatLabel} from '@angular/material/form-field';
import {Subject} from 'rxjs';
import {FacadeService} from "../../../shared/service-facade/facade.service";

/** Custom `MatFormFieldControl` for mat label tooltip. */
@Component({
  selector: 'mat-label-tooltip',
  templateUrl: 'mat-label-tooltip.component.html',
  styleUrls: ['mat-label-tooltip.component.css'],
  providers: [{provide: MatFormFieldControl, useExisting: MatLabelTooltipComponent}],
  host: {
    '[id]': 'id',
  },
})

export class MatLabelTooltipComponent implements ControlValueAccessor, MatFormFieldControl<MatLabel>, AfterViewInit, OnDestroy {
  @Input() matTooltip: string | null = null;
  @Input() tooltipText: string = "";
  stateChanges = new Subject<void>();
  focused = false;
  touched = false;
  controlType = 'mat-label-tooltip';
  id = "1";
  @Input('aria-describedby') userAriaDescribedBy: string | undefined;
  /**
   * This will be used to show tooltip or not
   * This can be used to show the tooltip conditionally
   */
  @Input() showToolTip: boolean = true;

  constructor(
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    @Optional() @Inject(MAT_FORM_FIELD) public _formField: MatFormField,
    @Optional() @Self() public ngControl: NgControl,
    private facadeService: FacadeService,
    private changeDetector: ChangeDetectorRef
  ) {
    this.id = this._elementRef.nativeElement.id;
    this._placeholder = "";

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  private _disabled = false;

  /**
   * Get if the control is disabled.
   */
  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  /**
   * Set if the control is disabled.
   */
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  private _required = false;

  /**
   * Get required field
   */
  @Input()
  get required(): boolean {
    return this._required;
  }

  /**
   * Set required field
   */
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  private _placeholder: string;

  /**
   * Place holder override get accessor
   */
  @Input()
  get placeholder(): string {
    return this._placeholder;
  }

  /**
   * Place holder override get accessor
   */
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }

  /**
   * Get if the tooltip is empty
   */
  get empty() {
    return this.matTooltip === null;
  }

  /**
   * Override for mat-label
   */
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  /**
   * Get the tooltip text for entire control
   */
  @Input()
  get value(): string | null {
    return this.matTooltip;
  }

  /**
   * Set the tooltip text for entire control
   */
  set value(text: string | null) {
    this.matTooltip = text;
    this.stateChanges.next();
  }

  /**
   * Override not used
   */
  get errorState(): boolean {
    return false;
  }

  /**
   * Init life cycle event handler
   */
  ngOnInit() {

    if (!this.showToolTip) {
      return;
    }

  }

  ngAfterViewInit(): void {
    let watchItem = this.facadeService.watchStorageItem(this.id + "-tooltip");
    if (watchItem != undefined) {
      watchItem.subscribe(setting => {
        this.tooltipText = setting?.replace("&#13;", '\r\n');

        this.changeDetector.detectChanges();
      });
    }

  }

  /**
   * Override not used
   * @param _
   */
  onChange = (_: any) => {
  };

  /**
   * Override not used
   */
  onTouched = () => {
  };

  /**
   * Called when control is destroyed
   */
  ngOnDestroy() {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  /**
   * Override for label
   * @param ids
   */
  setDescribedByIds(ids: string[]) {
    const controlElement = this._elementRef.nativeElement.querySelector(
      '.mat-label-tooltip-container',
    )!;
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  /**
   * Override not used
   */
  onContainerClick() {
  }

  /**
   * Sets the value
   * @param text
   */
  writeValue(text: string | null): void {
    this.value = text;
  }

  /**
   * Override not used.
   * @param fn
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * Override not used
   * @param fn
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Set the control to disabled
   * @param isDisabled True if the control is disabled.
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Dummy function to get pointer icon on mat-icon
   */
  onClick() {
  }

}
